import fileDownload from 'js-file-download';
import { useCallback } from 'react';
import {
  useExportChatQueryCsv,
  useIsExportingChatQueryCsv,
} from 'src/api/queries/chat/useExportChatQueryCsv';
import { chatCsvFileName, CSV_MIME_TYPE } from 'src/app/chat/chatCsv';
import { formatNumber, showToast } from 'src/utils';

export interface IChatQueryCsvDownload {
  download: () => Promise<void>;
  downloading: boolean;
}

/** @registry Saves one chat reply's query result as `fire-query-<local timestamp>.csv` and notes when the server capped the rows; `downloading` stays true across unmount and remount while the export runs; failures toast through the global mutation error pipeline. */
export const useChatQueryCsvDownload = (
  conversationId: string,
  auditId: string,
): IChatQueryCsvDownload => {
  const { mutateAsync } = useExportChatQueryCsv({ conversationId, auditId });
  const downloading = useIsExportingChatQueryCsv(auditId);

  const download = useCallback(async () => {
    const file = await mutateAsync().catch(() => null);
    if (!file) return;
    fileDownload(
      file.data,
      chatCsvFileName('query', new Date()),
      CSV_MIME_TYPE,
    );
    if (file.rowLimit !== null) {
      showToast(
        `The file contains the first ${formatNumber(file.rowLimit)} rows.`,
        { intent: 'primary', icon: 'info-sign' },
      );
    }
  }, [mutateAsync]);

  return { download, downloading };
};
